import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSimpleCan } from "../../../config/casl/casl";
import { queryClient } from "../../../config/react-query";
import { useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { setCurrentUser } from "../../../redux/reducers/userReducer";
import { store } from "../../../redux/store";
import { errorToast, successToast } from "../../../utils/toasts";
import { apiClient } from "../../../zodios/client";
import { css } from "../../../../styled-system/css";
import { TextField } from "../../../components/fields/base/TextField";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { RadioGroup } from "../../../components/fields/base/RadioGroup";
import { setPerformanceMetric as setReduxPerformanceMetric } from "../../../redux/reducers/userReducer";
import Loading from "../../../components/core/blocks/Loading";

const OrgSettingsSection = () => {
  const { t } = useTranslation(["settings", "common"]);
  const user = useAppSelector((state) => state.user);
  const displayOrganizationName = useAppSelector((state) => state.user.prefers_org_name_display);

  const [profileSelectorLabelField, setProfileSelectorLabelField] = useState<"name" | "profile_name">(
    displayOrganizationName ? "name" : "profile_name",
  );
  const [productIdentifier, setProductIdentifier] = useState<"asin" | "sku">("asin");
  const [performanceMetric, setPerformanceMetric] = useState<"acos" | "roas">("acos");

  const [orgName, setOrgName] = useState(user.organization_name);

  const userCanUpdateOrgSettings = useSimpleCan("update", "Org");

  const updateOrgNameMutation = useMutation({
    mutationFn: async () => {
      await apiClient.patchUserOrgName({ org_name: orgName });
    },
    onSuccess: () => {
      store.dispatch(setCurrentUser({ organization_name: orgName }));
      void queryClient.invalidateQueries({
        queryKey: [{ entity: "organizations" }],
      });
      successToast({
        message: t(($) => $.settings.org["Organization name successfully updated."]),
      });
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.settings.org["Failed to save your preferences, please retry."]), error });
    },
  });

  const createModuleHandler = useMutation({
    mutationFn: async (moduleData: { module_name: string }) => {
      return await apiClient.createModule(moduleData);
    },
    onSuccess: (res: { module_name: string; id: number }[]) => {
      successToast({
        message: t(($) => $.settings.org["Saved your preferences."]),
      });
      store.dispatch(
        setCurrentUser({
          prefers_org_name_display: profileSelectorLabelField === "name",
          modules: res.map((module) => ({ name: module.module_name, id: module.id, settings: null })),
        }),
      );
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.settings.org["Failed to save your preferences, please retry."]), error });
    },
  });

  const deleteModuleHandler = useMutation({
    mutationFn: async (moduleName: string) => {
      return await apiClient.deleteModule(undefined, { params: { module_name: moduleName } });
    },
    onSuccess: () => {
      successToast({
        message: t(($) => $.settings.org["Saved your preferences."]),
      });
      store.dispatch(setCurrentUser({ prefers_org_name_display: profileSelectorLabelField === "name" }));
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.settings.org["Failed to save your preferences, please retry."]), error });
    },
  });

  const updateUserPreferencesMutation = useMutation({
    mutationFn: async () => {
      const data = {
        product_identifier: productIdentifier,
        acos_or_roas: performanceMetric,
      };

      await apiClient.putUserPreferences(data);
    },

    onSuccess: () => {
      store.dispatch(setReduxPerformanceMetric(performanceMetric));

      void queryClient.invalidateQueries({
        queryKey: ["preferences", user.organization_id],
      });
      successToast({
        message: t(($) => $.settings.org["Saved your preferences."]),
      });
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.settings.org["Failed to save your preferences, please retry."]), error });
    },
  });

  const userPreferencesQuery = useQuery({
    queryKey: ["preferences", user.organization_id],
    queryFn: async () => {
      const res = await apiClient.getUserPreferences();

      setProductIdentifier(res["product_identifier"]);
      setPerformanceMetric(res["preferences"]["acos_or_roas"]);

      return res;
    },
    refetchOnMount: true,
  });

  return (
    <VStack css={{ gap: "medium", alignItems: "start", paddingY: "medium", fontSize: "medium" }}>
      <h4
        className={css({
          margin: 0,
          paddingX: "large",
          fontWeight: 500,
        })}
      >
        {t(($) => $.settings.org["Organization Settings"])}
      </h4>
      {userCanUpdateOrgSettings && (
        <div className={css({ width: "100%" })}>
          <VStack gap={0} alignItems={"start"} padding={"large"}>
            <TextField
              label={t(($) => $.settings.org["Organization Name"])}
              css={{ width: "100%" }}
              value={orgName}
              onChange={(value) => setOrgName(value)}
            />
          </VStack>
          <Flex className={css({ padding: "large" })}>
            <RadioGroup
              orientation={"horizontal"}
              label={t(($) => $.settings.org["Account Selector Field"])}
              items={[
                {
                  value: "profile_name",
                  label: t(($) => $.settings.org["Amazon Profile Name"]),
                },
                {
                  value: "name",
                  label: t(($) => $.settings.org["Organization Name"]),
                },
              ]}
              description={t(
                ($) =>
                  $.settings.org[
                    "Choose how account names appear in the dropdown. By default, the dropdown displays the Amazon profile name for each account, which is not editable. To use a custom label, select Organization Name and update it in the field above."
                  ],
              )}
              value={profileSelectorLabelField}
              onChange={(value) => {
                setProfileSelectorLabelField(value as "name" | "profile_name");
              }}
            />
          </Flex>
          <Flex justifyContent={"center"} paddingY={"medium"}>
            <Button
              onPress={() => {
                if (orgName && orgName !== user.organization_name) {
                  updateOrgNameMutation.mutate();
                }
                if ((profileSelectorLabelField === "name") !== displayOrganizationName) {
                  if (profileSelectorLabelField === "name") {
                    createModuleHandler.mutate({ module_name: "SHOW_ORG_NAMES" });
                  } else {
                    deleteModuleHandler.mutate("SHOW_ORG_NAMES");
                  }
                }
              }}
              isPending={
                updateOrgNameMutation.isPending || createModuleHandler.isPending || deleteModuleHandler.isPending
              }
            >
              {t(($) => $.common.actions.Save)}
            </Button>
          </Flex>
        </div>
      )}
      {userPreferencesQuery.isLoading ?
        <Loading fullPage={false} height={"500px"} />
      : <>
          <Flex className={css({ width: "100%", padding: "large" })}>
            <RadioGroup
              className={css({
                maxWidth: "50%",
              })}
              orientation={"horizontal"}
              label={t(($) => $.settings.org["Product Identifier"])}
              items={[
                {
                  value: "asin",
                  label: t(($) => $.settings.org.ASIN),
                },
                {
                  value: "sku",
                  label: t(($) => $.settings.org.SKU),
                },
              ]}
              value={productIdentifier}
              onChange={(value) => {
                setProductIdentifier(value as "asin" | "sku");
              }}
            />
          </Flex>
          <Flex className={css({ width: "100%", padding: "large" })}>
            <RadioGroup
              className={css({
                maxWidth: "50%",
              })}
              orientation={"horizontal"}
              label={t(($) => $.settings.org["Performance Metric"])}
              items={[
                {
                  value: "acos",
                  label: t(($) => $.settings.org.ACOS),
                },
                {
                  value: "roas",
                  label: t(($) => $.settings.org.ROAS),
                },
              ]}
              value={performanceMetric}
              onChange={(value) => {
                setPerformanceMetric(value as "acos" | "roas");
              }}
            />
          </Flex>
          <Flex justifyContent={"center"} width={"100%"} paddingY={"medium"}>
            <Button
              onPress={() => updateUserPreferencesMutation.mutate()}
              isDisabled={!userCanUpdateOrgSettings}
              isPending={updateUserPreferencesMutation.isPending}
            >
              {t(($) => $.common.actions.Save)}
            </Button>
          </Flex>
        </>
      }
    </VStack>
  );
};

export default OrgSettingsSection;
